import { ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET } from "@/lib/constants/wallet";
import type { Template, TemplateConnectorRequirement } from "@/stores/templatesStore";
import { getConditionalNodeIds } from "./conditionalNodes";

const POLYMARKET_WALLET_ACTIONS = new Set([
  "polymarket-place-bet",
  "polymarket-close-position",
  "polymarket-redeem",
  "polymarket-cancel-order",
  "polymarket-get-balance",
  "polymarket-get-open-orders",
  "polymarket-get-redeemable-positions",
  "polymarket-get-trade-history",
  "polymarket-get-builder-trades",
  "polymarket-get-positions",
  "polymarket-heartbeat",
  "polymarket-withdraw",
]);

/** Pure derivation — wrap in `useMemo` keyed on the `template` ref. */
export function getRequiredTemplateConnectors(template: Template): TemplateConnectorRequirement[] {
  if (!template?.definition?.nodes) return [];

  const { nodes } = template.definition;
  const conditionalNodeIds = getConditionalNodeIds(nodes);

  // Two-pass: first collect all connector types and whether any occurrence is unconditional.
  const typeHasUnconditional = new Map<string, boolean>();
  let hasPolymarketAction = false;
  for (const [nodeId, node] of Object.entries(nodes)) {
    if (POLYMARKET_WALLET_ACTIONS.has(node.type)) {
      hasPolymarketAction = true;
    }
    if (!node.connector?.type || node.connector.id) continue;
    const t = node.connector.type;
    if (!conditionalNodeIds.has(nodeId)) {
      typeHasUnconditional.set(t, true);
    } else if (!typeHasUnconditional.has(t)) {
      typeHasUnconditional.set(t, false);
    }
  }

  // Second pass: deduplicate by connector type, mark optional only if ALL occurrences are conditional.
  const seen = new Set<string>();
  const requirements: TemplateConnectorRequirement[] = [];
  for (const node of Object.values(nodes)) {
    if (node.connector?.type && !node.connector.id && !seen.has(node.connector.type)) {
      seen.add(node.connector.type);
      const isWallet = node.connector.type === "wallet";
      requirements.push({
        key: node.connector.type,
        type: node.connector.type,
        nodeType: node.connector.type,
        optional: typeHasUnconditional.get(node.connector.type) === false,
        ...(isWallet && hasPolymarketAction
          ? {
              accountType: ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET,
              label: "Polymarket Deposit Wallet",
              description: "Select your Polymarket deposit wallet, or set one up.",
            }
          : {}),
      });
    }
  }

  return requirements;
}
